'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewRun } from '@/data/reviews/use-review-statuses';
import type { ReviewRunStatus } from '@/lib/realtime/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { reviewRunsQueryKey } from '@/data/reviews/use-review-statuses';
async function cancelAiReview(params: {
  owner: string;
  repo: string;
  number: string;
  installationId: number;
  traceId: string;
}) {
  const {
    owner,
    repo,
    number,
    installationId,
    traceId
  } = params;
  const response = await fetch(`/api/github/repos/${owner}/${repo}/pulls/${number}/cancel-review`, {
    method: 'POST',
    body: JSON.stringify({
      installationId,
      traceId
    })
  });
  if (!response.ok) {
    throw new Error('Failed to cancel AI review');
  }
  return response.json();
}
export function useCancelAiReview() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = async variables => {
      const {
        owner,
        repo,
        number,
        traceId,
        suppressToast
      } = variables;
      if (!suppressToast) {
        toast.success("AI review cancelled");
      }
      const queryKey = reviewRunsQueryKey(owner, repo, number);
      await queryClient.cancelQueries({
        queryKey
      });
      const previousReviewRuns = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, t1 => {
        const oldData = t1 === undefined ? [] : t1;
        return oldData.map(run => run.traceId === traceId ? {
          ...run,
          status: "cancelled" as ReviewRunStatus
        } : run);
      });
      return {
        previousReviewRuns,
        queryKey: [...queryKey]
      };
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient) {
    t1 = (error, variables_0, context) => {
      console.error("Error canceling AI review:", error);
      if (!variables_0.suppressToast) {
        toast.error("Failed to cancel AI review");
      }
      if (context?.previousReviewRuns && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousReviewRuns);
      }
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: cancelAiReview,
      onMutate: t0,
      onSuccess: _temp,
      onError: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
}
function _temp() {}